import { useSortableTable } from '../../../hooks/useSortableTable';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AddSiteModal from '../../../AddSiteModal';
import { navigateWithPreference } from '../../../utils/navigationHelper';
import { apiFetch } from '../../../utils/api';
import PageHeader from '../../../components/PageHeader';

function Sites() {
  const navigate = useNavigate();
  const [sites, setSites] = useState([]);
  const { items: sortedSites, requestSort, getSortIndicator } = useSortableTable(sites, { key: 'site_name', direction: 'asc' });
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const columnMenuRef = useRef(null);

  const [visibleColumns, setVisibleColumns] = useState({
    siteName: true,
    customer: true,
    siteRef: false,
    contact: true,
    phone: true,
    mobile: false,
    email: false,
    address: true,
    city: true,
    county: false,
    postcode: false,
    accessNotes: false,
    isPrimary: false,
  });

  const columns = [
    { key: 'siteName', label: 'Site Name' },
    { key: 'customer', label: 'Customer' },
    { key: 'siteRef', label: 'Site Ref' },
    { key: 'contact', label: 'Contact Name' },
    { key: 'phone', label: 'Phone' },
    { key: 'mobile', label: 'Mobile' },
    { key: 'email', label: 'Email' },
    { key: 'address', label: 'Address' },
    { key: 'city', label: 'City' },
    { key: 'county', label: 'County' },
    { key: 'postcode', label: 'Postcode' },
    { key: 'accessNotes', label: 'Access Notes' },
    { key: 'isPrimary', label: 'Primary Site' },
  ];

  useEffect(() => {
    fetchSites();
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      const response = await apiFetch('/api/preferences/default/sites');
      const data = await response.json();
      if (data.preferences) setVisibleColumns(data.preferences);
    } catch (error) {
      console.error('Error loading preferences:', error);
    }
  };

  const savePreferences = async (newPreferences) => {
    try {
      await apiFetch('/api/preferences/default/sites', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preferences: newPreferences })
      });
    } catch (error) {
      console.error('Error saving preferences:', error);
    }
  };

  useEffect(() => {
    savePreferences(visibleColumns);
  }, [visibleColumns]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (columnMenuRef.current && !columnMenuRef.current.contains(event.target)) {
        setShowColumnMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchSites = async () => {
    try {
      const response = await apiFetch('/api/sites');
      const data = await response.json();
      setSites(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching sites:', error);
      setSites([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSiteAdded = () => {
    fetchSites();
    setShowAddModal(false);
  };

  const toggleColumn = (columnKey) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  const filteredSites = sortedSites.filter(site =>
    site.site_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    site.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    site.city?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const visibleColumnCount = Object.values(visibleColumns).filter(Boolean).length + 1;

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Sites"
        subtitle={`${sites.length} total`}
        backButton
        onBack={() => navigate('/crm')}
        backTitle="Back to CRM"
        sticky
        actionButton={
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm"
          >
            ➕ Add a Site
          </button>
        }
      />

      <div className="px-4 py-3">
        <div className="mb-3 flex items-center space-x-2">
          <input
            type="text"
            placeholder="Search sites..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 max-w-md px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-green-500 focus:border-green-500"
          />

          <div className="relative" ref={columnMenuRef}>
            <button
              onClick={() => setShowColumnMenu(!showColumnMenu)}
              className="px-3 py-1.5 bg-gray-300 hover:bg-gray-400 text-black rounded text-sm font-medium transition flex items-center space-x-1"
            >
              <span>⚙️</span>
              <span>Columns</span>
            </button>

            {showColumnMenu && (
              <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 w-48">
                <div className="p-2">
                  <div className="text-xs font-semibold text-gray-600 mb-2 px-2">Show Columns</div>
                  {columns.map(column => (
                    <label
                      key={column.key}
                      className="flex items-center px-2 py-1.5 hover:bg-gray-50 rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={visibleColumns[column.key]}
                        onChange={() => toggleColumn(column.key)}
                        className="mr-2 rounded border-gray-300 text-green-600 focus:ring-green-500"
                      />
                      <span className="text-sm text-gray-700">{column.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded border border-gray-200">
          <div className="overflow-auto max-h-[calc(100vh-240px)] relative">
            <table className="w-full text-sm">
              <thead className="bg-gray-300 sticky top-0 z-20 border-b border-gray-400">
                <tr>
                  {visibleColumns.siteName && <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('site_name')}>Site Name{getSortIndicator('site_name')}</th>}
                  {visibleColumns.customer && <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('company_name')}>Customer{getSortIndicator('company_name')}</th>}
                  {visibleColumns.siteRef && <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('site_ref')}>Site Ref{getSortIndicator('site_ref')}</th>}
                  {visibleColumns.contact && <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('site_contact')}>Contact{getSortIndicator('site_contact')}</th>}
                  {visibleColumns.phone && <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('site_phone')}>Phone{getSortIndicator('site_phone')}</th>}
                  {visibleColumns.mobile && <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('mobile')}>Mobile{getSortIndicator('mobile')}</th>}
                  {visibleColumns.email && <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('site_email')}>Email{getSortIndicator('site_email')}</th>}
                  {visibleColumns.address && <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('address')}>Address{getSortIndicator('address')}</th>}
                  {visibleColumns.city && <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('city')}>City{getSortIndicator('city')}</th>}
                  {visibleColumns.county && <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('county')}>County{getSortIndicator('county')}</th>}
                  {visibleColumns.postcode && <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('postcode')}>Postcode{getSortIndicator('postcode')}</th>}
                  {visibleColumns.accessNotes && <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600">Access Notes</th>}
                  {visibleColumns.isPrimary && <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600">Primary</th>}
                  <th className="text-right px-3 py-1.5 text-xs font-semibold text-gray-600">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan={visibleColumnCount} className="px-3 py-6 text-center text-gray-500 text-sm">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600 mx-auto mb-2"></div>
                      Loading...
                    </td>
                  </tr>
                ) : filteredSites.length === 0 ? (
                  <tr>
                    <td colSpan={visibleColumnCount} className="px-3 py-6 text-center text-gray-500 text-sm">
                      {searchTerm ? 'No matches.' : 'No sites yet.'}
                    </td>
                  </tr>
                ) : (
                  filteredSites.map((site, index) => (
                    <tr
                      key={site.id}
                      onClick={() => navigateWithPreference(navigate, `/crm/sites/${site.id}`, 'sites')}
                      className={`hover:bg-blue-200 cursor-pointer transition ${index % 2 === 1 ? 'bg-blue-100' : 'bg-white'}`}
                    >
                      {visibleColumns.siteName && <td className="px-3 py-2"><div className="font-medium text-gray-900">{site.site_name}</div></td>}
                      {visibleColumns.customer && <td className="px-3 py-2 text-gray-700">{site.company_name || '-'}</td>}
                      {visibleColumns.siteRef && <td className="px-3 py-2 text-gray-700">{site.site_ref || '-'}</td>}
                      {visibleColumns.contact && <td className="px-3 py-2 text-gray-700">{site.site_contact || '-'}</td>}
                      {visibleColumns.phone && <td className="px-3 py-2 text-gray-700">{site.site_phone || '-'}</td>}
                      {visibleColumns.mobile && <td className="px-3 py-2 text-gray-700">{site.mobile || '-'}</td>}
                      {visibleColumns.email && <td className="px-3 py-2 text-gray-700">{site.site_email || '-'}</td>}
                      {visibleColumns.address && <td className="px-3 py-2 text-gray-700">{site.address || '-'}</td>}
                      {visibleColumns.city && <td className="px-3 py-2 text-gray-700">{site.city || '-'}</td>}
                      {visibleColumns.county && <td className="px-3 py-2 text-gray-700">{site.county || '-'}</td>}
                      {visibleColumns.postcode && <td className="px-3 py-2 text-gray-700">{site.postcode || '-'}</td>}
                      {visibleColumns.accessNotes && <td className="px-3 py-2 text-gray-700 text-xs">{site.access_notes ? (site.access_notes.length > 50 ? site.access_notes.substring(0, 50) + '...' : site.access_notes) : '-'}</td>}
                      {visibleColumns.isPrimary && <td className="px-3 py-2 text-center">{site.is_primary ? '✓' : ''}</td>}
                      <td className="px-3 py-2 text-right">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/crm/sites/${site.id}`);
                          }}
                          className="text-green-600 hover:text-green-800 text-xs font-medium mr-3"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-2 text-xs text-gray-600">
          Showing {filteredSites.length} of {sites.length}
        </div>
      </div>

      {showAddModal && (
        <AddSiteModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSiteAdded={handleSiteAdded}
        />
      )}
    </div>
  );
}

export default Sites;
